/**
 * Settings > Extensions > Basecamp > Listening
 * Per-project webhook enable/disable + health.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Loader2, Radio, AlertTriangle, CircleOff, RefreshCw, Search } from 'lucide-react'
import { fetchWithAuth } from '@/lib/api'
import { useBasecampProjects } from '@/hooks/use-basecamp'
import { toast } from 'sonner'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import { SettingsSection } from '@/components/features/settings/settings-section'

type Health = 'connected' | 'degraded' | 'disconnected'

interface SubscriptionRow {
  bucket_id: string
  project_name: string | null
  active: boolean
  last_delivery_at: string | null
  last_error: string | null
  remote_active: boolean | null
}

interface StatusResponse {
  success: boolean
  health: Health
  active_count: number
  last_delivery_at: string | null
  payload_url: string | null
  subscriptions: SubscriptionRow[]
  error?: string
}

interface BasecampListeningPanelProps {
  organizationId: string
}

function HealthIcon({ health }: { health: Health }) {
  if (health === 'connected') return <Radio className="h-4 w-4 text-green-500" />
  if (health === 'degraded') return <AlertTriangle className="h-4 w-4 text-yellow-500" />
  return <CircleOff className="h-4 w-4 text-muted-foreground" />
}

export function BasecampListeningPanel({ organizationId }: BasecampListeningPanelProps) {
  const { projects, isLoading: projectsLoading } = useBasecampProjects(organizationId)
  const [status, setStatus] = useState<StatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [filter, setFilter] = useState('')

  const loadStatus = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(
        `/api/basecamp/webhook/status?organizationId=${encodeURIComponent(organizationId)}`,
        { headers: { 'x-organization-id': organizationId } }
      )
      const data = (await res.json()) as StatusResponse
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load Listening status')
      }
      setStatus(data)
    } catch (err) {
      console.error('[Listening]', err)
      toast.error(err instanceof Error ? err.message : 'Listening status failed')
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  const activeMap = new Map(
    (status?.subscriptions || []).filter((s) => s.active).map((s) => [String(s.bucket_id), s])
  )

  const filteredProjects = useMemo(() => {
    const list = projects || []
    const needle = filter.trim().toLowerCase()
    const listeningIds = new Set(
      (status?.subscriptions || [])
        .filter((s) => s.active)
        .map((s) => String(s.bucket_id))
    )
    const matched = needle
      ? list.filter(
          (p) =>
            p.name.toLowerCase().includes(needle) || String(p.id).includes(needle)
        )
      : list
    // Listening-on first, then A-Z
    return [...matched].sort((a, b) => {
      const aOn = listeningIds.has(String(a.id)) ? 0 : 1
      const bOn = listeningIds.has(String(b.id)) ? 0 : 1
      if (aOn !== bOn) return aOn - bOn
      return a.name.localeCompare(b.name)
    })
  }, [projects, filter, status?.subscriptions])

  const toggle = async (projectId: string, enable: boolean) => {
    setBusyId(projectId)
    try {
      const path = enable
        ? '/api/basecamp/webhook/register'
        : `/api/basecamp/webhook/unregister?project_id=${encodeURIComponent(projectId)}&organizationId=${encodeURIComponent(organizationId)}`
      const res = await fetchWithAuth(path, {
        method: enable ? 'POST' : 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'x-organization-id': organizationId,
        },
        body: enable
          ? JSON.stringify({ project_id: projectId, organizationId })
          : undefined,
      })
      const data = await res.json()
      if (!res.ok || data.success === false) {
        throw new Error(data.error || 'Listening update failed')
      }
      toast.success(enable ? 'Listening on' : 'Listening off')
      await loadStatus()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Listening update failed')
    } finally {
      setBusyId(null)
    }
  }

  const repair = async (projectId: string) => {
    setBusyId(projectId)
    try {
      const res = await fetchWithAuth('/api/basecamp/webhook/repair', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-organization-id': organizationId,
        },
        body: JSON.stringify({ project_id: projectId, organizationId }),
      })
      const data = await res.json()
      if (!res.ok || !data.success) throw new Error(data.error || 'Repair failed')
      toast.success('Webhook repaired')
      await loadStatus()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Repair failed')
    } finally {
      setBusyId(null)
    }
  }

  const health = status?.health || 'disconnected'

  const healthBadgeClass =
    health === 'connected'
      ? 'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400'
      : health === 'degraded'
        ? 'border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400'
        : 'border-border bg-muted/50 text-muted-foreground'

  return (
    <SettingsSection
      data-testid={testIds.workspaceSettings.basecampListeningPanel}
      icon={<HealthIcon health={health} />}
      title="Listening"
      badge={
        <Badge variant="outline" className={cn('text-xs capitalize font-medium', healthBadgeClass)}>
          {health}
        </Badge>
      }
      description="Basecamp posts Todo + Comment events to Maater. Activity feed and flows use these deliveries. Types: Todo, Comment."
      actions={
        <Button size="sm" variant="ghost" className="shrink-0 -mr-1" onClick={loadStatus} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      }
    >
      {status?.payload_url ? (
        <p className="break-all font-mono text-[11px] text-muted-foreground -mt-2">
          {status.payload_url}
        </p>
      ) : (
        <p className="text-xs text-yellow-600 -mt-2">
          Set API_PUBLIC_URL so Basecamp can reach the webhook endpoint.
        </p>
      )}

      {(projectsLoading || loading) && !projects?.length ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading projects...
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter projects..."
              className="h-9 pl-8 text-xs"
              data-testid={testIds.workspaceSettings.basecampListeningFilter}
              aria-label="Filter Basecamp projects"
            />
          </div>
          <div className="flex items-center justify-between px-0.5 text-[11px] text-muted-foreground">
            <span>
              {filteredProjects.length === (projects || []).length
                ? `${filteredProjects.length} projects`
                : `${filteredProjects.length} of ${(projects || []).length} projects`}
            </span>
            {filter.trim() ? (
              <button
                type="button"
                className="hover:text-foreground"
                onClick={() => setFilter('')}
              >
                Clear
              </button>
            ) : null}
          </div>
          <ul className="max-h-80 space-y-2.5 overflow-y-auto pr-0.5">
            {filteredProjects.length === 0 ? (
              <li className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
                No matching projects
              </li>
            ) : (
              filteredProjects.map((p) => {
                const id = String(p.id)
                const sub = activeMap.get(id)
                const on = !!sub
                const busy = busyId === id
                return (
                  <li
                    key={id}
                    className={cn(
                      'flex items-center justify-between gap-3 rounded-md border px-3.5 py-3 text-sm',
                      on ? 'border-green-500/30 bg-green-500/5' : 'border-border bg-background/60'
                    )}
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{p.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {on
                          ? sub?.last_delivery_at
                            ? `Last delivery ${new Date(sub.last_delivery_at).toLocaleString()}`
                            : 'Waiting for first delivery'
                          : 'Off'}
                        {sub?.last_error ? ` - ${sub.last_error}` : ''}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {on && (sub?.last_error || sub?.remote_active === false) && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => repair(id)}
                        >
                          Repair
                        </Button>
                      )}
                      <Switch
                        checked={on}
                        disabled={busy}
                        onCheckedChange={(checked) => toggle(id, checked)}
                        aria-label={`Listening for ${p.name}`}
                      />
                    </div>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      )}
    </SettingsSection>
  )
}
